import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPlayersFromUserClubs } from '../services/clubs';
import { createGame } from '../services/games';
import { createGuestPlayer, findGuestPlayers } from '../services/players';
import { useAuth } from '../contexts/AuthContext';
import { Player } from '../types';
import './NewGame.css';

const NewGame: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    playerA: '',
    playerB: '',
    framesToPlay: 5,
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchTermA, setSearchTermA] = useState('');
  const [searchTermB, setSearchTermB] = useState('');
  const [showDropdownA, setShowDropdownA] = useState(false);
  const [showDropdownB, setShowDropdownB] = useState(false);
  
  // Guest Player State
  const [playerBType, setPlayerBType] = useState<'member' | 'guest'>('member');
  const [guestName, setGuestName] = useState('');
  const [guestPreName, setGuestPreName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [existingGuests, setExistingGuests] = useState<Player[]>([]);
  const [showGuestSuggestions, setShowGuestSuggestions] = useState(false);
  
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isProfileComplete, player: currentPlayer } = useAuth();
  
  // Filter players with complete profiles
  const completePlayers = players.filter(player => 
    player.name?.trim() && player.preName?.trim() && player.pin?.trim()
  );

  // Add current player to the list if their profile is complete
  const allSelectablePlayers = useMemo(() => {
    const selectablePlayers = [...completePlayers];
    
    // Add current player if their profile is complete and not already in the list
    if (currentPlayer && 
        currentPlayer.name?.trim() && 
        currentPlayer.preName?.trim() && 
        currentPlayer.pin?.trim() &&
        !completePlayers.some(p => p.id === currentPlayer.id)) {
      // Add current player at the beginning for easy access
      selectablePlayers.unshift(currentPlayer);
    }
    
    // Always ensure current player is at the top if they're in the list
    if (currentPlayer && selectablePlayers.some(p => p.id === currentPlayer.id)) {
      const currentPlayerIndex = selectablePlayers.findIndex(p => p.id === currentPlayer.id);
      if (currentPlayerIndex > 0) {
        const [currentPlayerObj] = selectablePlayers.splice(currentPlayerIndex, 1);
        selectablePlayers.unshift(currentPlayerObj);
      }
    }
    
    return selectablePlayers;
  }, [completePlayers, currentPlayer]);

  // Filter players based on search terms and prioritize current player
  const filteredPlayersA = useMemo(() => {
    // Remove player B from the list if selected
    const availablePlayers = allSelectablePlayers.filter(player => 
      !formData.playerB || player.id.toString() !== formData.playerB
    );
    
    if (!searchTermA.trim()) return availablePlayers;
    
    const filtered = availablePlayers.filter(player =>
      `${player.preName} ${player.name}`.toLowerCase().includes(searchTermA.toLowerCase()) ||
      player.name?.toLowerCase().includes(searchTermA.toLowerCase()) ||
      player.preName?.toLowerCase().includes(searchTermA.toLowerCase())
    );
    
    // Sort so current player appears first if they match the search
    return filtered.sort((a, b) => {
      if (a.id === currentPlayer?.id) return -1;
      if (b.id === currentPlayer?.id) return 1;
      return 0;
    });
  }, [allSelectablePlayers, searchTermA, currentPlayer, formData.playerB]);

  const filteredPlayersB = useMemo(() => {
    // Remove player A from the list if selected
    const availablePlayers = allSelectablePlayers.filter(player => 
      !formData.playerA || player.id.toString() !== formData.playerA
    );
    
    if (!searchTermB.trim()) return availablePlayers;
    
    const filtered = availablePlayers.filter(player =>
      `${player.preName} ${player.name}`.toLowerCase().includes(searchTermB.toLowerCase()) ||
      player.name?.toLowerCase().includes(searchTermB.toLowerCase()) ||
      player.preName?.toLowerCase().includes(searchTermB.toLowerCase())
    );
    
    // Sort so current player appears first if they match the search
    return filtered.sort((a, b) => {
      if (a.id === currentPlayer?.id) return -1;
      if (b.id === currentPlayer?.id) return 1;
      return 0;
    });
  }, [allSelectablePlayers, searchTermB, currentPlayer, formData.playerA]);

  // Get selected player objects
  const selectedPlayerA = allSelectablePlayers.find(p => p.id.toString() === formData.playerA);
  const selectedPlayerB = allSelectablePlayers.find(p => p.id.toString() === formData.playerB);

  useEffect(() => {
    loadPlayers();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.player-selector')) {
        setShowDropdownA(false);
        setShowDropdownB(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadPlayers = async () => {
    try {
      setLoading(true);
      setError(null);
      // Get players from clubs that the current user belongs to
      const playersData = await getPlayersFromUserClubs();
      setPlayers(playersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load players');
    } finally {
      setLoading(false);
    }
  };

  // Check if current user is one of the selected players
  // For guest players, current user must be Player A
  const isCurrentUserSelected = useMemo(() => {
    if (!currentPlayer) return false;
    
    // If Player B is a guest, current user must be Player A
    if (playerBType === 'guest') {
      return formData.playerA === currentPlayer.id.toString();
    }
    
    // For member vs member, current user must be either A or B
    return formData.playerA === currentPlayer.id.toString() || 
           formData.playerB === currentPlayer.id.toString();
  }, [formData.playerA, formData.playerB, currentPlayer, playerBType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Validate player A is selected
    if (!formData.playerA) {
      setError(t('newGame.selectPlayerA'));
      setSubmitting(false);
      return;
    }

    // Validate player B (either selected or guest name provided)
    if (playerBType === 'member' && !formData.playerB) {
      setError(t('newGame.selectPlayerB'));
      setSubmitting(false);
      return;
    }

    if (playerBType === 'guest' && !guestName.trim()) {
      setError(t('newGame.guestNameRequired'));
      setSubmitting(false);
      return;
    }

    if (formData.playerA === formData.playerB) {
      setError(t('newGame.differentPlayersRequired'));
      setSubmitting(false);
      return;
    }

    // Current user must be selected (as Player A if playing against guest)
    if (!isCurrentUserSelected) {
      setError(t('newGame.currentUserRequired'));
      setSubmitting(false);
      return;
    }

    try {
      let playerBId: number;

      if (playerBType === 'guest') {
        // Create or reuse guest player with email
        const guest = await createGuestPlayer(
          guestName.trim(), 
          guestPreName.trim() || undefined,
          guestEmail.trim() || undefined
        );
        playerBId = guest.id;
      } else {
        playerBId = parseInt(formData.playerB);
      }

      const game = await createGame({
        playerA: parseInt(formData.playerA),
        playerB: playerBId,
        framesToPlay: formData.framesToPlay,
      });
      
      navigate(`/game/${game.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create game');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handlePlayerSelect = (playerId: string, playerType: 'A' | 'B') => {
    if (playerType === 'A') {
      setFormData(prev => ({ ...prev, playerA: playerId }));
      setSearchTermA('');
      setShowDropdownA(false);
    } else {
      setFormData(prev => ({ ...prev, playerB: playerId }));
      setSearchTermB('');
      setShowDropdownB(false);
    }
  };

  const handleSearchChange = (value: string, playerType: 'A' | 'B') => {
    if (playerType === 'A') {
      setSearchTermA(value);
      setShowDropdownA(value.length > 0);
    } else {
      setSearchTermB(value);
      setShowDropdownB(value.length > 0);
    }
  };

  const clearPlayer = (playerType: 'A' | 'B') => {
    if (playerType === 'A') {
      setFormData(prev => ({ ...prev, playerA: '' }));
      setSearchTermA('');
    } else {
      setFormData(prev => ({ ...prev, playerB: '' }));
      setSearchTermB('');
    }
  };

  const selectCurrentPlayer = (playerType: 'A' | 'B') => {
    if (currentPlayer) {
      handlePlayerSelect(currentPlayer.id.toString(), playerType);
    }
  };

  // Guest player functions
  const handleGuestNameChange = async (value: string) => {
    setGuestName(value);
    
    // Search for existing guests with similar name
    if (value.trim().length >= 2) {
      try {
        const guests = await findGuestPlayers(value.trim());
        setExistingGuests(guests);
        setShowGuestSuggestions(guests.length > 0);
      } catch (err) {
        console.error('Error finding guest players:', err);
        setExistingGuests([]);
      }
    } else {
      setExistingGuests([]);
      setShowGuestSuggestions(false);
    }
  };

  const selectExistingGuest = (guest: Player) => {
    setGuestName(guest.name);
    setGuestPreName(guest.preName || '');
    setGuestEmail(guest.email || '');
    setShowGuestSuggestions(false);
    setExistingGuests([]);
  };

  if (loading) {
    return (
      <div className="new-game-container">
        <div className="loading">Loading players...</div>
      </div>
    );
  }

  // Check if profile is complete
  if (!isProfileComplete) {
    return (
      <div className="new-game-container">
        <div className="new-game-header">
          <h1>{t('newGame.title')}</h1>
        </div>
        <div className="profile-incomplete-warning">
          <div className="warning-icon">⚠️</div>
          <h3>{t('newGame.profileIncompleteTitle')}</h3>
          <p>{t('newGame.profileIncompleteMessage')}</p>
          <button 
            onClick={() => navigate('/profile')} 
            className="btn btn-primary"
          >
            {t('newGame.completeProfile')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="new-game-container">
      <div className="new-game-header">
        <h1>{t('newGame.title')}</h1>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {!isCurrentUserSelected && (formData.playerA || formData.playerB || playerBType === 'guest') && (
        <div className="warning-message">
          <div className="warning-icon">⚠️</div>
          <p>{t('newGame.currentUserRequiredMessage')}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="new-game-form">
        <div className="form-group">
          <label htmlFor="playerA">{t('newGame.playerA')}</label>
          <div className="player-selector">
            <div className="player-input-container">
              <input
                type="text"
                placeholder={selectedPlayerA ? `${selectedPlayerA.preName} ${selectedPlayerA.name}` : t('newGame.selectPlayerA')}
                value={searchTermA}
                onChange={(e) => handleSearchChange(e.target.value, 'A')}
                onFocus={() => setShowDropdownA(true)}
                disabled={submitting}
                className="player-search-input"
              />
              <div className="input-buttons">
                {currentPlayer && 
                 currentPlayer.name?.trim() && 
                 currentPlayer.preName?.trim() && 
                 currentPlayer.pin?.trim() && 
                 formData.playerA !== currentPlayer.id.toString() && 
                 (!showDropdownA || filteredPlayersA.length === 0 || !filteredPlayersA.some(p => p.id === currentPlayer.id)) && (
                  <button
                    type="button"
                    onClick={() => selectCurrentPlayer('A')}
                    className="select-self-icon"
                    disabled={submitting}
                    title={t('newGame.selectSelf')}
                  >
                    👤
                  </button>
                )}
                {selectedPlayerA && (
                  <button
                    type="button"
                    onClick={() => clearPlayer('A')}
                    className="clear-player-btn"
                    disabled={submitting}
                    title={t('newGame.clearSelection')}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            
            {showDropdownA && (
              <div className="player-dropdown">
                {filteredPlayersA.length > 0 ? (
                  filteredPlayersA.map(player => (
                    <div
                      key={player.id}
                      className={`player-option ${formData.playerA === player.id.toString() ? 'selected' : ''}`}
                      onClick={() => handlePlayerSelect(player.id.toString(), 'A')}
                    >
                      <span className="player-name">{player.preName} {player.name}</span>
                      {player.id === currentPlayer?.id && (
                        <span className="current-player-badge">You</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-results">{t('newGame.noPlayersFound')}</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="playerB">{t('newGame.playerB')}</label>
          
          {/* Player Type Selector */}
          <div className="player-type-selector">
            <label className={`type-option ${playerBType === 'member' ? 'active' : ''}`}>
              <input
                type="radio"
                name="playerBType"
                value="member"
                checked={playerBType === 'member'}
                onChange={() => {
                  setPlayerBType('member');
                  setGuestName('');
                  setGuestPreName('');
                  setGuestEmail('');
                  setShowGuestSuggestions(false);
                }}
                disabled={submitting}
              />
              <span>{t('newGame.clubMember')}</span>
            </label>
            
            <label className={`type-option ${playerBType === 'guest' ? 'active' : ''}`}>
              <input
                type="radio"
                name="playerBType"
                value="guest"
                checked={playerBType === 'guest'}
                onChange={() => {
                  setPlayerBType('guest');
                  setFormData(prev => ({ ...prev, playerB: '' }));
                  setSearchTermB('');
                }}
                disabled={submitting}
              />
              <span>{t('newGame.guestPlayer')}</span>
            </label>
          </div>

          {/* Club Member Selection */}
          {playerBType === 'member' && (
            <div className="player-selector">
              <div className="player-input-container">
                <input
                  type="text"
                  placeholder={selectedPlayerB ? `${selectedPlayerB.preName} ${selectedPlayerB.name}` : t('newGame.selectPlayerB')}
                  value={searchTermB}
                  onChange={(e) => handleSearchChange(e.target.value, 'B')}
                  onFocus={() => setShowDropdownB(true)}
                  disabled={submitting}
                  className="player-search-input"
                />
                <div className="input-buttons">
                  {currentPlayer && 
                   currentPlayer.name?.trim() && 
                   currentPlayer.preName?.trim() && 
                   currentPlayer.pin?.trim() && 
                   formData.playerB !== currentPlayer.id.toString() && 
                   (!showDropdownB || filteredPlayersB.length === 0 || !filteredPlayersB.some(p => p.id === currentPlayer.id)) && (
                    <button
                      type="button"
                      onClick={() => selectCurrentPlayer('B')}
                      className="select-self-icon"
                      disabled={submitting}
                      title={t('newGame.selectSelf')}
                    >
                      👤
                    </button>
                  )}
                  {selectedPlayerB && (
                    <button
                      type="button"
                      onClick={() => clearPlayer('B')}
                      className="clear-player-btn"
                      disabled={submitting}
                      title={t('newGame.clearSelection')}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              
              {showDropdownB && (
                <div className="player-dropdown">
                  {filteredPlayersB.length > 0 ? (
                    filteredPlayersB.map(player => (
                      <div
                        key={player.id}
                        className={`player-option ${formData.playerB === player.id.toString() ? 'selected' : ''}`}
                        onClick={() => handlePlayerSelect(player.id.toString(), 'B')}
                      >
                        <span className="player-name">{player.preName} {player.name}</span>
                        {player.id === currentPlayer?.id && (
                          <span className="current-player-badge">You</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="no-results">{t('newGame.noPlayersFound')}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Guest Player Input */}
          {playerBType === 'guest' && (
            <div className="guest-player-inputs">
              <div className="guest-input-row">
                <input
                  type="text"
                  placeholder={t('newGame.guestPreName')}
                  value={guestPreName}
                  onChange={(e) => setGuestPreName(e.target.value)}
                  disabled={submitting}
                  className="guest-prename-input"
                />
                <div className="guest-name-container">
                  <input
                    type="text"
                    placeholder={t('newGame.guestName')}
                    value={guestName}
                    onChange={(e) => handleGuestNameChange(e.target.value)}
                    disabled={submitting}
                    className="guest-name-input"
                    required={playerBType === 'guest'}
                  />
                  {showGuestSuggestions && existingGuests.length > 0 && (
                    <div className="guest-suggestions">
                      <div className="suggestion-header">
                        {t('newGame.existingGuests')}
                      </div>
                      {existingGuests.map(guest => (
                        <div
                          key={guest.id}
                          className="guest-suggestion-item"
                          onClick={() => selectExistingGuest(guest)}
                        >
                          <span className="guest-icon">👤</span>
                          <span className="guest-name">{guest.preName} {guest.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="guest-email-row">
                <input
                  type="email"
                  placeholder={t('newGame.guestEmail')}
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  disabled={submitting}
                  className="guest-email-input"
                />
              </div>
              <div className="guest-hint">
                {t('newGame.guestEmailHint')}
              </div>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="framesToPlay">{t('newGame.framesToPlay')}</label>
          <input
            type="number"
            id="framesToPlay"
            name="framesToPlay"
            value={formData.framesToPlay}
            onChange={handleChange}
            min="1"
            max="21"
            required
            disabled={submitting}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/games')}
            className="btn btn-secondary"
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !isCurrentUserSelected}
          >
            {submitting ? t('newGame.creating') : t('newGame.createGame')}
          </button>
        </div>
      </form>

      {/* Show message if no players are available */}
      {players.length === 0 && !loading && (
        <div className="no-complete-players-warning">
          <div className="warning-icon">⚠️</div>
          <h3>{t('newGame.noClubMembersTitle')}</h3>
          <p>{t('newGame.noClubMembersMessage')}</p>
        </div>
      )}
      
      {/* Show message if no complete players are available */}
      {players.length > 0 && allSelectablePlayers.length === 0 && !loading && (
        <div className="no-complete-players-warning">
          <div className="warning-icon">⚠️</div>
          <h3>{t('newGame.noCompletePlayersTitle')}</h3>
          <p>{t('newGame.noCompletePlayersMessage')}</p>
        </div>
      )}
    </div>
  );
};

export default NewGame;

